import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import clsx from 'clsx';
import { isValid } from 'date-fns';
import { CSSProperties, FocusEvent, Ref, useMemo, useState } from 'react';
import { RangeValue } from 'react-aria';
import { DateRangePicker as AriaDateRangePicker, DateInput, DateSegment, Group } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import { useDefaultRanges } from '~/components/date-pickers/MonthRangePicker/hooks/useDefaultRanges';
import CalendarRangePopover, { PresetDate } from '~/components/inputs/DatePicker/CalendarRangePopover';
import { VStack } from '~/components/layout/Stack';
import CalendarIcon from '~/icons/icon-calendar.svg?react';
import CloseIcon from '~/icons/icon-close.svg?react';
import { DateRange } from '~/types/date';
import { dateToCalendarDate, useFormatRange } from '~/utils/date';
import styles from './VATRegistrationRow.module.scss';

interface Props {
  'aria-label'?: string;
  className?: string;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  maxValue?: Date | undefined;
  minValue?: Date | undefined;
  onChange: (value: DateRange) => void;
  style?: CSSProperties;
  value: DateRange | null;
  ref?: Ref<HTMLDivElement>;
}

const PurchasePeriodTableField = ({
  'aria-label': ariaLabel,
  className,
  id,
  isDisabled,
  isInvalid,
  maxValue,
  minValue,
  onChange,
  style,
  value,
  ref,
}: Props) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'registrations.create.form' });
  const [isOpen, setIsOpen] = useState<boolean>();
  const [manualInput, setManualInput] = useState<RangeValue<CalendarDate> | null>(null);

  const presetValue =
    value?.startDate && value.endDate
      ? { start: dateToCalendarDate(value.startDate), end: dateToCalendarDate(value.endDate) }
      : undefined;

  const defaultRanges = useDefaultRanges(new Date(), false);
  const formatRange = useFormatRange();

  const presetDates: PresetDate[] = useMemo(
    () =>
      defaultRanges.map((range, index) => ({
        id: `preset-${index}`,
        label: formatRange(range.startDate, range.endDate),
        date: {
          start: range.startDate!,
          end: range.endDate!,
        },
      })),
    [defaultRanges, formatRange],
  );

  const handleFieldBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (isOpen) return;
    if (!e.currentTarget.contains(e.relatedTarget) && manualInput) {
      onChange({
        startDate: manualInput.start.toDate(getLocalTimeZone()),
        endDate: manualInput.end.toDate(getLocalTimeZone()),
      });
      setManualInput(null);
    }
  };

  return (
    <AriaDateRangePicker
      ref={ref}
      aria-label={ariaLabel ?? t('dynamic_field_purchasePeriod')}
      style={style}
      id={id}
      onOpenChange={setIsOpen}
      isOpen={isOpen}
      onChange={setManualInput}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      className={className}
      minValue={minValue ? dateToCalendarDate(minValue) : undefined}
      maxValue={maxValue ? dateToCalendarDate(maxValue) : undefined}
      value={manualInput ?? presetValue ?? null}
      shouldForceLeadingZeros
    >
      <Group className={styles.dateWrapper} onBlur={handleFieldBlur}>
        <VStack className={styles.dateInfo}>
          <DateInput className={clsx(styles.dateInput, { [styles.unset!]: !isValid(value?.startDate) })} slot="start">
            {(segment) => <DateSegment className={styles.dateSegment} segment={segment} />}
          </DateInput>
          <DateInput className={clsx(styles.dateInput, { [styles.unset!]: !isValid(value?.endDate) })} slot="end">
            {(segment) => <DateSegment className={styles.dateSegment} segment={segment} />}
          </DateInput>
        </VStack>
        {(manualInput ?? presetValue) && (
          <IconButton
            Icon={CloseIcon}
            variant="ghost"
            size="small"
            onPressStart={() => {
              onChange({ startDate: undefined, endDate: undefined });
              setManualInput(null);
            }}
          />
        )}
        <IconButton Icon={CalendarIcon} variant="ghost" size="medium" />
      </Group>
      <CalendarRangePopover
        presetDates={presetDates}
        onSelectDate={(val) => {
          if (val.start && val.end) {
            onChange({ startDate: val.start, endDate: val.end });
          }
          setManualInput(null);
          setIsOpen(false);
        }}
      />
    </AriaDateRangePicker>
  );
};

export default PurchasePeriodTableField;
